/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LegalRegion, PsChapter, PsTreeSearchConditions } from '../../../../api/model';
import { useGetPsChapterList } from '../../../../api/pss/pss';
import { useDefaultOptions } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import { toString } from '../../../../utils/string';
import { CheckBox } from '../../../atoms/CheckBox';
import DatePicker from '../../../atoms/DatePicker';
import ErrorComponent from '../../../atoms/ErrorComponent';
import SingleSelect from '../../../atoms/SingleSelect';
import { TextInput } from '../../../atoms/TextInput';
import WaitComponent from '../../../atoms/WaitComponent';
import Modal from '../../../modules/Modal';
import PsLegalRegionSelectDialog from './PsLegalRegionSelectDialog';
import { psTreeSearchConditionsSchema } from './constants/variables';

interface Props {
  /** モーダル表示・非表示状態。 */
  isOpen: boolean;

  /** モーダル表示・非表示状態設定関数。 */
  setIsOpen: (isOpen: boolean) => void;

  /** postPsTreeSearch 用の検索条件 PsTreeSearchConditions を設定する関数。 */
  setPsTreeSearchConditions: (newPsTreeSearchConditions: PsTreeSearchConditions) => void;

  // /** 法規適用国指定ダイアログ表示・非表示状態設定関数。 */
  // setIsPsLegalRegionSelectDialogOpen: (isOpen: boolean) => void;

  // /** 法規適用国指定ダイアログで選択された LegalRegion の配列。 */
  // legalRegions: LegalRegion[];
}

// PS 閲覧画面で使用。
/** (PS ツリー) 表示オプション ダイアログ。 */
function PsTreeDisplayOptionsDialog({
  isOpen,
  setIsOpen,
  setPsTreeSearchConditions,
  // setIsPsLegalRegionSelectDialogOpen,
  // legalRegions,
}: Props) {
  // i18n 用。
  const { t, i18n } = useTranslation();

  // API 用。
  const defaultOptions = useDefaultOptions(i18n.language);

  // React Hook Form。
  const { register, control, formState, getValues, reset } = useForm<PsTreeSearchConditions>({
    // schema から検索条件のデフォルト値を取得し渡す。
    defaultValues: psTreeSearchConditionsSchema.getDefault(),
    resolver: yupResolver(
      psTreeSearchConditionsSchema.concat(
        yup.object().shape({
          // half() と date() は /src/frontend/app/src/index.tsx の yup.addMethod 時に追加されるため、ここで定義し直す。
          derivation_texts: yup.array().of(yup.string().half()).default([]),
          update_at_from: yup.string().date().default(''),
          update_at_to: yup.string().date().default(''),
        })
      )
    ),
    criteriaMode: 'all',
    mode: 'all',
  });

  // ps_nos 用。
  const {
    fields: psNoFields,
    append: appendPsNo,
    remove: removePsNo,
  } = useFieldArray({
    control,
    name: 'ps_nos' as never,
  });

  // derivation_texts 用。
  const {
    fields: derivationTextFields,
    append: appendDerivationText,
    remove: removeDerivationText,
  } = useFieldArray({
    control,
    name: 'derivation_texts' as never,
  });

  // legal_infos 用。
  const {
    fields: legalInfoFields,
    append: appendLegalInfo,
    remove: removeLegalInfo,
  } = useFieldArray({
    control,
    name: 'legal_infos' as never,
  });

  // text_conditions 用。
  const {
    fields: textConditionFields,
    append: appendTextCondition,
    remove: removeTextCondition,
  } = useFieldArray({
    control,
    name: 'text_conditions' as never,
  });

  // judgement_infos 用。
  const {
    fields: judgementInfoFields,
    append: appendJudgementInfo,
    remove: removeJudgementInfo,
  } = useFieldArray({
    control,
    name: 'judgement_infos' as never,
  });

  // /** watch する legal_region_ids。 */
  // const legalRegionIds = useWatch({
  //   control,
  //   name: 'legal_region_ids'
  // });

  /** 法規適用国指定ダイアログ表示・非表示 state。 */
  const [isPsLegalRegionSelectDialogOpen, setIsPsLegalRegionSelectDialogOpen] = useState<boolean>(false);

  /** LegalRegion の配列。法規適用国指定ダイアログで設定する。 */
  const [legalRegions, setLegalRegions] = useState<LegalRegion[]>([]);


  // PS 章一覧取得 (「章の絞り込み」ドロップダウン表示用)。
  // API 呼び出し。
  const getPsChapterListResponse = useGetPsChapterList(defaultOptions);

  // console.log('ps_chapters: ', getPsChapterListResponse.data?.data.ps_chapters)

  // 表示するレベル。
  const levelOptions = new Array(5).fill(0).map((element: number, index: number) => ({
    label: `L${index + 1}`,
    value: index + 1,
  }));

  // 検索種別の option の配列。
  const operatorOptions = [
    {
      value: 0,
      label: t('AND'),
    },
    {
      value: 1,
      label: t('OR'),
    },
  ];

  const getOperatorOptionValue = (operatorOption: typeof operatorOptions[number]) => operatorOption.value;
  const getOperatorOptionLabel = (operatorOption: typeof operatorOptions[number]) => operatorOption.label;

  return (
    <>
      <Modal
        active={isOpen}
        additionalClassName='popupfr865'
        displayCloseButton
        onClose={
          () => {
            setIsOpen(false);
          }
        }
      >
        {
          // モーダル内部にメッセージを表示。
          (getPsChapterListResponse.isLoading) ?
            (
              <WaitComponent />
            )
            :
            (
              getPsChapterListResponse.isError
              ||
              !(getPsChapterListResponse.data)
              ||
              !(getPsChapterListResponse.data.data)
              ||
              !(getPsChapterListResponse.data.data.ps_chapters)
            ) ?
              (
                <ErrorComponent error={getPsChapterListResponse.error} />
              )
              :
              (
                <>
                  <div className="pop-tablefr2">
                    <table className="poptable1 w230">
                      <tbody>
                        <tr>
                          <th>{t('タイトルPSグループを含む')}</th>
                          <td>
                            <CheckBox
                              name='include_title_ps_group'
                              register={register}
                              label={t('チェックを付けると条件にマッチしたPSを含むタイトルPSは配下の全PSを結果に含めます')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('フリーワード検索')}</label>
                          </th>
                          <td>
                            <TextInput
                              name='freeword'
                              register={register}
                              errors={formState.errors}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('章の絞り込み')}</label>
                          </th>
                          <td>
                            <span>
                              <SingleSelect
                                name='chapter_ps_id'
                                allowEmpty={false}
                                options={[
                                  {
                                    chapter_ps_id: 0,
                                    chapter_ps_no: t('すべて'),
                                  },
                                  ...getPsChapterListResponse.data.data.ps_chapters,
                                ]}
                                register={register}
                                getValue={
                                  // 「すべて」の場合は '' (空文字列) を API に送る。
                                  (psChapter: PsChapter) =>
                                    psChapter.chapter_ps_no === t('すべて') ? '' : psChapter.chapter_ps_id
                                }
                                getLabel={(psChapter: PsChapter) => psChapter.chapter_ps_no}
                              />
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('表示するレベル（L1〜L5）の範囲')}</label>
                          </th>
                          <td>
                            <CheckBox
                              name='title_only'
                              register={register}
                              label={t('タイトルのみ')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <span className="l1select">
                              <span>
                                <SingleSelect
                                  name='level_from'
                                  allowEmpty={false}
                                  options={levelOptions}
                                  register={register}
                                  getValue={(levelOption) => levelOption.value}
                                  getLabel={(levelOption) => levelOption.label}
                                />
                              </span>
                              〜{' '}
                              <span>
                                <SingleSelect
                                  name='level_to'
                                  allowEmpty={false}
                                  options={levelOptions}
                                  register={register}
                                  getValue={(levelOption) => levelOption.value}
                                  getLabel={(levelOption) => levelOption.label}
                                />
                              </span>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('PS状態')}</label>
                          </th>
                          <td>
                            <CheckBox
                              name='ps_state_approved'
                              register={register}
                              label={t('承認済み')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='ps_state_request'
                              register={register}
                              label={t('申請中')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='ps_state_deleted'
                              register={register}
                              label={t('廃止済み')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('記載条件分類で絞り込み')}</label>
                          </th>
                          <td>
                            <CheckBox
                              name='text_type_danger'
                              register={register}
                              label={t('危険')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='text_type_warning'
                              register={register}
                              label={t('警告')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='text_type_caution'
                              register={register}
                              label={t('注意')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='text_type_notice'
                              register={register}
                              label={t('アドバイス')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('PS管理No.検索')}</label>
                          </th>
                          <td>
                            {
                              psNoFields.map(
                                (psNo: Record<'id', string>, index: number) =>
                                (
                                  <div key={psNo.id} className="inputfr">
                                    <TextInput
                                      name={`ps_nos.${index}`}
                                      register={register}
                                      errors={formState.errors}
                                    />
                                    {
                                      (index === 0)
                                      &&
                                      <>{t('OR検索')}&nbsp;&nbsp;&nbsp;</>
                                    }
                                    <span className="cursor" onClick={() => removePsNo(index)}>
                                      <i className="fa-solid fa-circle-xmark" />
                                      {t('削除')}
                                    </span>
                                  </div>
                                )
                              )
                            }

                            <span className="cursor" onClick={() => appendPsNo('')}>
                              <i className="fa-solid fa-circle-plus" />
                              {t('追加')}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('派生文章を検索')}</label>
                          </th>
                          <td>
                            {derivationTextFields.map((derivationText: Record<'id', string>, index: number) => (
                              <div key={derivationText.id} className="mb5">
                                <TextInput
                                  name={`derivation_texts.${index}`}
                                  register={register}
                                  errors={formState.errors}
                                />
                                {
                                  (index === 0)
                                  &&
                                  (
                                    <span className="l1select">
                                      <SingleSelect
                                        name='derivation_search_type'
                                        allowEmpty={false}
                                        options={operatorOptions}
                                        register={register}
                                        getValue={getOperatorOptionValue}
                                        getLabel={getOperatorOptionLabel}
                                      />
                                    </span>
                                  )
                                }
                                <span
                                  className="cursor"
                                  style={{
                                    marginLeft: '5px',
                                  }}
                                  onClick={() => removeDerivationText(index)}
                                >
                                  <i className="fa-solid fa-circle-xmark" />
                                  {t('削除')}
                                </span>
                              </div>
                            ))}

                            <span className="cursor" onClick={() => appendDerivationText('')}>
                              <i className="fa-solid fa-circle-plus" />
                              {t('追加')}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('法規情報検索')}</label>
                          </th>
                          <td>
                            <div className="inputfr">
                              <CheckBox
                                name='legal_ps'
                                register={register}
                                label={t('法規対象PSで絞り込み')}
                              />
                            </div>

                            {
                              legalInfoFields.map(
                                (legalInfo: Record<'id', string>, index: number) => (
                                  <div key={legalInfo.id} className="inputfr">
                                    <TextInput
                                      name={`legal_infos.${index}`}
                                      register={register}
                                      errors={formState.errors}
                                    />
                                    {
                                      (index === 0)
                                      &&
                                      (
                                        <>{t('OR検索')}&nbsp;&nbsp;&nbsp;</>
                                      )
                                    }
                                    <span className="cursor" onClick={() => removeLegalInfo(index)}>
                                      <i className="fa-solid fa-circle-xmark" />
                                      {t('削除')}
                                    </span>
                                  </div>
                                )
                              )
                            }

                            <span className="cursor" onClick={() => appendLegalInfo('')}>
                              <i className="fa-solid fa-circle-plus" />
                              {t('追加')}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('法規適用国')}</label>
                          </th>
                          <td>
                            <span
                              onClick={() => {
                                // 法規適用国指定ダイアログを開く。
                                setIsPsLegalRegionSelectDialogOpen(true);
                              }}
                              className='cursor'
                            >
                              <i className="fa-solid fa-pen-to-square" />
                              {t('法規適用国選択')}
                            </span>
                            <span
                              style={{
                                marginLeft: '1rem',
                              }}
                            >
                              {
                                // 選択された法規適用国名をカンマ区切りで表示。
                                legalRegions.map((legalRegion: LegalRegion) => legalRegion.legal_region_name).join()
                              }
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('テキスト確定条件検索')}</label>
                          </th>
                          <td>
                            {
                              textConditionFields.map(
                                (textCondition: Record<'id', string>, index: number) =>
                                (
                                  <div key={textCondition.id} className="mb5">
                                    <TextInput
                                      name={`text_conditions.${index}`}
                                      register={register}
                                      errors={formState.errors}
                                    />
                                    {
                                      (index === 0)
                                      &&
                                      (
                                        <span className="l1select">
                                          <SingleSelect
                                            name='text_condition_search_type'
                                            allowEmpty={false}
                                            options={operatorOptions}
                                            register={register}
                                            getValue={getOperatorOptionValue}
                                            getLabel={getOperatorOptionLabel}
                                          />
                                        </span>
                                      )
                                    }
                                    <span
                                      className="cursor"
                                      style={{
                                        marginLeft: '5px',
                                      }}
                                      onClick={() => removeTextCondition(index)}
                                    >
                                      <i className="fa-solid fa-circle-xmark" />
                                      {t('削除')}
                                    </span>
                                  </div>
                                )
                              )
                            }

                            <span
                              className="cursor"
                              onClick={() => appendTextCondition('')}
                            >
                              <i className="fa-solid fa-circle-plus" />
                              {t('追加')}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('判断情報（図面、Flist等）')}</label>
                          </th>
                          <td>
                            {
                              judgementInfoFields.map(
                                (judgementInfo: Record<'id', string>, index: number) =>
                                (
                                  <div
                                    key={judgementInfo.id}
                                    className="mb5"
                                  >
                                    <TextInput
                                      name={`judgement_infos.${index}`}
                                      register={register}
                                      errors={formState.errors}
                                    />
                                    {
                                      (index === 0)
                                      &&
                                      (
                                        <span className="l1select">
                                          <SingleSelect
                                            name='judgement_info_search_type'
                                            allowEmpty={false}
                                            options={operatorOptions}
                                            register={register}
                                            getValue={getOperatorOptionValue}
                                            getLabel={getOperatorOptionLabel}
                                          />
                                        </span>
                                      )
                                    }
                                    <span
                                      className="cursor"
                                      style={{
                                        marginLeft: '5px',
                                      }}
                                      onClick={() => removeJudgementInfo(index)}
                                    >
                                      <i className="fa-solid fa-circle-xmark" />
                                      {t('削除')}
                                    </span>
                                  </div>
                                )
                              )
                            }

                            <span className="cursor" onClick={() => appendJudgementInfo('')}>
                              <i className="fa-solid fa-circle-plus" />
                              {t('追加')}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <label>{t('最終更新日で絞り込み')}</label>
                          </th>
                          <td>
                            <DatePicker
                              name='update_at_from'
                              isDisabled={false}
                              control={control}
                              displayMark
                            />
                            {
                              (formState.errors.update_at_from?.message)
                              &&
                              (
                                <>
                                  <br />
                                  <span className="form-error">
                                    {formState.errors.update_at_from?.message}
                                  </span>
                                  <br />
                                </>
                              )
                            }
                            〜{' '}
                            <DatePicker
                              name='update_at_to'
                              isDisabled={false}
                              control={control}
                              displayMark
                            />
                            {
                              (formState.errors.update_at_to?.message)
                              &&
                              (
                                <>
                                  <br />
                                  <span className="form-error">
                                    {formState.errors.update_at_to?.message}
                                  </span>
                                  <br />
                                </>
                              )
                            }
                          </td>
                        </tr>

                        <tr>
                          <th>{t('その他')}</th>
                          <td>
                            <CheckBox
                              name='service_spec_table'
                              register={register}
                              label={t('事前定型質問が必要')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='tos'
                              register={register}
                              label={t('その他トス情報が必要')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='hg_check_require'
                              register={register}
                              label={t('開発部門確認')}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>{t('FSR ID検索')}</th>
                          <td>
                            <div
                              style={{
                                marginBottom: '0.5rem',
                              }}
                            >
                              <CheckBox
                                name='fsr_id_search'
                                register={register}
                                label={t('FSR IDの対象PSで絞り込み')}
                              />
                            </div>
                            <TextInput
                              name='fsr_id'
                              register={register}
                              errors={formState.errors}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>{t('移行先PS管理No.')}</th>
                          <td>
                            <div
                              style={{
                                marginBottom: '0.5rem',
                              }}
                            >
                              <CheckBox
                                name='next_ps_no_search'
                                register={register}
                                label={t('移行先PSNo.が設定されているPSで絞り込み')}
                              />
                            </div>
                            <TextInput
                              name='next_ps_no'
                              register={register}
                              errors={formState.errors}
                            />
                          </td>
                        </tr>
                        <tr>
                          <th>{t('テキスト分類で絞り込み')}</th>
                          <td>
                            <div className="mb5">
                              <CheckBox
                                name='content_type_body'
                                register={register}
                                label={t('本文')}
                              />
                              <span
                                style={{
                                  marginRight: '1rem',
                                }}
                              />
                              <CheckBox
                                name='content_type_body_sup'
                                register={register}
                                label={t('本文補足')}
                              />
                              <span
                                style={{
                                  marginRight: '1rem',
                                }}
                              />
                              <CheckBox
                                name='content_type_work'
                                register={register}
                                label={t('作動する条件')}
                              />
                              <span
                                style={{
                                  marginRight: '1rem',
                                }}
                              />
                              <CheckBox
                                name='content_type_not_work'
                                register={register}
                                label={t('作動しない条件')}
                              />
                            </div>
                            <CheckBox
                              name='content_type_custom'
                              register={register}
                              label={t('カスタマイズ')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='content_type_notice'
                              register={register}
                              label={t('注意・警告')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='content_type_instruct'
                              register={register}
                              label={t('指示事項')}
                            />
                            <span
                              style={{
                                marginRight: '1rem',
                              }}
                            />
                            <CheckBox
                              name='content_type_case'
                              register={register}
                              label={t('こんなときは')}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="btnfr4">
                    <div>
                      <span
                        className="mr15"
                        onClick={() => {
                          reset();
                        }}
                      >
                        <input
                          type="button"
                          value={toString(t('クリア'))}
                          className="btn1 btnbg-g"
                        />
                      </span>

                      <span
                        className="btn2 btnbg-g mr15"
                        onClick={() => {
                          setIsOpen(false);
                        }}
                      >
                        <span className="modal-close2">
                          {t('キャンセル')}
                        </span>
                      </span>

                      <span>
                        <input
                          type="button"
                          value={toString(t('絞り込み'))}
                          className="btn1"
                          // disabled={!formState.isDirty || !formState.isValid}
                          // disabled={!formState.isValid}
                          onClick={() => {
                            // PsDetailsPage で定義された state の psTreeSearchConditions が PsTreeDisplayOptionsDialog で変更され、
                            // PsTree に渡され、PsTree で API の postPsTreeSearch を呼ぶという流れ。
                            setPsTreeSearchConditions({
                              ...getValues(),
                              legal_region_ids: legalRegions.map(
                                (legalRegion: LegalRegion) => legalRegion.legal_region_id as number
                              ),
                            });
                            setIsOpen(false);
                          }}
                        />
                      </span>
                    </div>
                  </div>
                </>
              )
        }
      </Modal>

      {/* 法規適用国指定ダイアログ。 */}
      <PsLegalRegionSelectDialog
        isOpen={isPsLegalRegionSelectDialogOpen}
        setIsOpen={
          (newIsOpen: boolean) => {
            setIsPsLegalRegionSelectDialogOpen(newIsOpen);
          }
        }
        setLegalRegions={
          (newLegalRegions: LegalRegion[]) => {
            setLegalRegions(newLegalRegions);
          }
        }
      />
    </>
  );
}

export default PsTreeDisplayOptionsDialog;
